/**
 * Authentication state management store
 * Handles user authentication state and Azure AD user info
 */

import { createStore, createSelector, Store } from './store';

/**
 * User role from Azure AD
 * USER is for users without any specific Azure AD role assignment
 */
export type UserRole = 'USER' | 'ASSISTANT' | 'ADMIN';

/**
 * Authenticated user information from Azure AD / MS Graph
 */
export interface User {
  /** Azure AD user ID (UUID) */
  id: string;
  /** User's email address */
  email: string;
  /** User's display name */
  displayName: string;
  /** User's first name */
  givenName: string;
  /** User's last name */
  surname: string;
  /** User's job title */
  jobTitle?: string;
  /** User's department */
  department?: string;
  /** Manager's Azure AD user ID (for approval workflow) */
  managerId?: string;
  /** Manager's display name */
  managerDisplayName?: string;
  /** User's role from Azure AD */
  role: UserRole;
  /** User's profile photo URL (optional) */
  photoUrl?: string;
}

/**
 * Authentication state shape
 */
export interface AuthState {
  /** Whether authentication is being checked */
  isLoading: boolean;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Authenticated user information */
  user: User | null;
  /** Access token for API calls */
  accessToken: string | null;
  /** Token expiration timestamp */
  tokenExpiresAt: number | null;
  /** Authentication error message */
  error: string | null;
}

/**
 * Initial authentication state
 */
const initialAuthState: AuthState = {
  isLoading: true,
  isAuthenticated: false,
  user: null,
  accessToken: null,
  tokenExpiresAt: null,
  error: null,
};

/**
 * Auth store with session-based persistence
 */
const authStore: Store<AuthState> = createStore<AuthState>(initialAuthState, {
  persistKey: 'wyden-auth-state',
  useSessionStorage: true,
  // Only persist non-sensitive parts of state
  serialize: (state) => JSON.stringify({
    isAuthenticated: state.isAuthenticated,
    user: state.user,
    // Don't persist token for security
  }),
  deserialize: (raw) => {
    const parsed = JSON.parse(raw);
    return {
      ...initialAuthState,
      isLoading: false,
      isAuthenticated: parsed.isAuthenticated ?? false,
      user: parsed.user ?? null,
    };
  },
});

// ============================================================================
// Selectors (derived state)
// ============================================================================

/**
 * Select whether the user is currently loading
 */
export const selectIsLoading = (state: AuthState): boolean => state.isLoading;

/**
 * Select whether the user is authenticated
 */
export const selectIsAuthenticated = (state: AuthState): boolean => state.isAuthenticated;

/**
 * Select the current user
 */
export const selectUser = (state: AuthState): User | null => state.user;

/**
 * Select the user's role
 */
export const selectUserRole = (state: AuthState): UserRole | null => state.user?.role ?? null;

/**
 * Select whether the user is an admin
 */
export const selectIsAdmin = createSelector<AuthState, UserRole | null, boolean>(
  selectUserRole,
  (role): boolean => role === 'ADMIN'
);

/**
 * Select whether the user is a Team Assistant
 */
export const selectIsAssistant = createSelector<AuthState, UserRole | null, boolean>(
  selectUserRole,
  (role): boolean => role === 'ASSISTANT'
);

/**
 * Select the user's display name
 */
export const selectDisplayName = (state: AuthState): string => state.user?.displayName ?? '';

/**
 * Select the user's manager ID (for approval workflow)
 */
export const selectManagerId = (state: AuthState): string | null => state.user?.managerId ?? null;

/**
 * Select whether the access token is expired
 */
export const selectIsTokenExpired = (state: AuthState): boolean => {
  if (!state.tokenExpiresAt) return true;
  return Date.now() >= state.tokenExpiresAt;
};

/**
 * Select any authentication error
 */
export const selectAuthError = (state: AuthState): string | null => state.error;

// ============================================================================
// Actions (state mutations)
// ============================================================================

/**
 * Set loading state during auth check
 */
export function setAuthLoading(isLoading: boolean): void {
  authStore.setState({ isLoading, error: null });
}

/**
 * Set authenticated user after successful login
 */
export function setAuthenticated(user: User, accessToken: string, expiresIn: number): void {
  authStore.setState({
    isLoading: false,
    isAuthenticated: true,
    user,
    accessToken,
    tokenExpiresAt: Date.now() + expiresIn * 1000,
    error: null,
  });
}

/**
 * Update user information (e.g., after profile update)
 */
export function updateUser(updates: Partial<User>): void {
  const currentUser = authStore.getState().user;
  if (currentUser) {
    authStore.setState({
      user: { ...currentUser, ...updates },
    });
  }
}

/**
 * Update access token (e.g., after token refresh)
 */
export function updateAccessToken(accessToken: string, expiresIn: number): void {
  authStore.setState({
    accessToken,
    tokenExpiresAt: Date.now() + expiresIn * 1000,
  });
}

/**
 * Set authentication error
 */
export function setAuthError(error: string): void {
  authStore.setState({
    isLoading: false,
    error,
  });
}

/**
 * Clear authentication error
 */
export function clearAuthError(): void {
  authStore.setState({ error: null });
}

/**
 * Logout and clear all auth state
 */
export function logout(): void {
  authStore.reset(initialAuthState);
  authStore.setState({ isLoading: false });
}

// ============================================================================
// Store exports
// ============================================================================

/**
 * Get current auth state (readonly)
 */
export function getAuthState(): Readonly<AuthState> {
  return authStore.getState();
}

/**
 * Subscribe to auth state changes
 */
export const subscribeToAuth = authStore.subscribe.bind(authStore);

/**
 * Subscribe to selected auth state
 */
export const subscribeToAuthSelector = authStore.subscribeToSelector.bind(authStore);

/**
 * Get access token (helper for API calls)
 */
export function getAccessToken(): string | null {
  const state = authStore.getState();
  if (!state.isAuthenticated || selectIsTokenExpired(state)) {
    return null;
  }
  return state.accessToken;
}

export { authStore };
